import { useMemo } from "react";
import ActionWrapper from "Common/DataTable/ActionWrapper";
import EditBtn from "Common/DataTable/EditBtn";
import DeleteBtn from "Common/DataTable/DeleteBtn";
import ViewBtn from "Common/DataTable/ViewBtn";
import PrintBtn from "Common/DataTable/PrintBtn";
import { Link } from "react-router-dom";
import { getImageUrl } from "utils/imageHelper";
import { PhotoProvider, PhotoView } from "react-photo-view";
import "react-photo-view/dist/react-photo-view.css";

const useTableConfig = ({
  data = [],
  page = 1,
  limit = 10,
  canShowAction = false,
  refetch = () => { },
  config = {},
  IsModel = false,
  IsEdit = true,
  IsDelete = true,
  isView = false,
  isPrint = false,
  IsEditDisble = false,
  IsEditDisbleKey = "",
  IsDeleteDisble = false,
  IsDeleteDisbleKey = "",
}) => {
  const {
    entityName,
    editUrl,
    idKey,
    displayFields,
    deleteApi,
    ViewUrl = "",
    PrintApi,
    PrintKeys,
  } = config;

  const formattedData = useMemo(() => {
    return data.map((item, index) => {
      const row = { rowNo: (page - 1) * limit + index + 1 };
      displayFields.forEach((field) => {
        row[field.accessor] = item[field.dataKey];
      });
      if (IsEditDisble) row[IsEditDisbleKey] = item[IsEditDisbleKey];
      if (IsDeleteDisble) row[IsDeleteDisbleKey] = item[IsDeleteDisbleKey];
      row[idKey] = item[idKey];
      return row;
    });
  }, [
    IsEditDisble,
    IsEditDisbleKey,
    IsDeleteDisble,
    IsDeleteDisbleKey,
    data,
    page,
    limit,
    displayFields,
    idKey,
  ]);

  const columns = useMemo(() => {
    const baseColumns = [
      { Header: "No", accessor: "rowNo" },
      ...displayFields.map(({ Header, accessor, isImage }) => ({
        Header,
        accessor,
        Cell: ({ value, column }) => {
          if (value === null || value === undefined || value === "") return isImage ? null : "N/A";

          // Only process as image/doc if specifically marked as isImage
          if (isImage) {
            const isString = typeof value === "string";
            const isDoc =
              isString &&
              (value.toLowerCase().endsWith(".pdf") ||
                value.toLowerCase().endsWith(".doc") ||
                value.toLowerCase().endsWith(".docx"));

            if (isDoc) {
              return (
                <a
                  href={getImageUrl(value)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-decoration-none"
                >
                  <i
                    className={`bi ${value.toLowerCase().endsWith(".pdf") ? "bi-filetype-pdf text-danger" : "bi-filetype-doc"} fs-2`}
                    style={{
                      color: value.toLowerCase().endsWith(".pdf")
                        ? "#dc3545"
                        : "#103F91",
                    }}
                  ></i>
                </a>
              );
            }

            return (
              <PhotoProvider>
                <PhotoView src={getImageUrl(value)}>
                  <img
                    src={getImageUrl(value)}
                    className="img-fluid avatar lg rounded"
                    alt={column.Header || "Thumbnail"}
                    style={{ cursor: "pointer" }}
                    onError={(e) => {
                      e.currentTarget.src =
                        "data:image/svg+xml;charset=utf8,<svg xmlns='http://www.w3.org/2000/svg' width='80' height='80'><rect width='100%' height='100%' fill='%23eee'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' font-size='12' fill='%23999'>no image</text></svg>";
                    }}
                  />
                </PhotoView>
              </PhotoProvider>
            );
          }

          return value;
        },
      })),
    ];

    if (
      !canShowAction ||
      (!IsEdit && !IsDelete && !isView && !IsModel && !isPrint)
    )
      return baseColumns;

    return [
      ...baseColumns,
      {
        Header: "Action",
        accessor: "action",
        sortable: false,
        Cell: ({ row }) => (
          <ActionWrapper>
            {IsEdit && (
              <EditBtn
                url={`${editUrl}/${row.original[idKey]}`}
                disabled={row.original[IsEditDisbleKey]}
              />
            )}
            {isView && (
              <ViewBtn
                url={`${ViewUrl}/${row.original[idKey]}`}
              />
            )}
            {isPrint && (
              <PrintBtn
                id={row.original[idKey]}
                apiPrintFunction={PrintApi}
                printkeysjson={PrintKeys}
                title="Pool Booking Receipt"
              />
            )}
            {IsDelete && (
              <DeleteBtn
                id={row.original[idKey]}
                fetchData={refetch}
                apiDeleteFunction={deleteApi}
                itemName={entityName}
                disabled={row.original[IsDeleteDisbleKey]}
              />
            )}
            {IsModel?.show && (
              <Link
                to={`${editUrl}/${row.original[idKey]}`}
                id={row.original[idKey]}
                className="btn btn-primary"
              >
                {IsModel.val}
              </Link>
            )}
          </ActionWrapper>
        ),
      },
    ];
  }, [
    IsModel,
    isPrint,
    displayFields,
    canShowAction,
    refetch,
    editUrl,
    idKey,
    entityName,
    deleteApi,
    IsEdit,
    IsDelete,
    isView,
    ViewUrl,
  ]);

  return { formattedData, columns };
};

export default useTableConfig;
